import React, { useLayoutEffect, useRef, useState, useMemo, useCallback } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { alignRootToWord } from "../../utils/rootAlignment";

// A distinct colour per radical position — the SAME colour marks the letter in the
// word and its tile in the root, so the eye connects "this letter → this radical".
const RADICAL_COLORS = ["#1535BD", "#0E9F6E", "#C2410C", "#7C3AED", "#B45309"];

const WORD_TILE = 44;
const WORD_GAP = 6;
const MIN_WORD_TILE = 20;

/**
 * Tile size and gap (px) that fit `count` word tiles on ONE row of `width` px.
 * Long words shrink instead of wrapping: a second row would send the connectors
 * between rows and behind tiles. The gap shrinks in proportion to the tile.
 * Below MIN_WORD_TILE the row overflows (centred) rather than become illegible.
 */
export const fitWordTiles = (count, width) => {
  if (!count || !width) return { size: WORD_TILE, gap: WORD_GAP };
  const fit = Math.floor((width * WORD_TILE) / (count * WORD_TILE + (count - 1) * WORD_GAP));
  const size = Math.max(MIN_WORD_TILE, Math.min(WORD_TILE, fit));
  return { size, gap: Math.floor((size * WORD_GAP) / WORD_TILE) };
};

const prefersReducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

// Position of `el` relative to `container`, using layout offsets (NOT transforms),
// so connector geometry is correct even while the tiles are mid-animation.
const relRect = (el, container) => {
  let x = 0;
  let y = 0;
  let node = el;
  let guard = 0;
  while (node && node !== container && guard < 30) {
    x += node.offsetLeft;
    y += node.offsetTop;
    node = node.offsetParent;
    guard += 1;
  }
  if (node !== container) return null; // container wasn't an offset ancestor
  return { x, y, w: el.offsetWidth, h: el.offsetHeight };
};

/**
 * RootReveal — an animated, colour-coded diagram that teaches how a surface word
 * is built from its Arabic root: it highlights the root's radical letters inside
 * the word, draws a connector from each to its position in the root, and lets the
 * learner jump to the root. Falls back to a plain root chip if the letters can't
 * be aligned. `onExplore(root)` navigates to the root. `tentative` captions it as a
 * possible root rather than "its root", for when root analysis can't tell which of
 * several roots the word is from (a homograph).
 */
const RootReveal = ({ word, root, onExplore, tentative = false }) => {
  const align = useMemo(() => alignRootToWord(word, root), [word, root]);
  const [playKey, setPlayKey] = useState(0);
  const [beams, setBeams] = useState([]);
  const [rowWidth, setRowWidth] = useState(0);
  const reduced = useMemo(prefersReducedMotion, []);

  const containerRef = useRef(null);
  const wordRefs = useRef([]); // by word-letter index
  const rootRefs = useRef([]); // by radical index

  // Timing (ms).
  const n = align ? align.letters.length : 0;
  const WORD_BASE = 140;
  const WORD_STAGGER = 60;
  const WORD_DUR = 320;
  const wordInEnd = WORD_BASE + Math.max(0, n - 1) * WORD_STAGGER + WORD_DUR;
  const ROOT_BASE = wordInEnd + 220;
  const ROOT_STAGGER = 190;

  const { size: wordTile, gap: wordGap } = fitWordTiles(n, rowWidth);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container || !align) return;
    const next = [];
    for (const rm of align.root) {
      const wEl = wordRefs.current[rm.wordIndex];
      const rEl = rootRefs.current[rm.radicalIndex];
      if (!wEl || !rEl) continue;
      const a = relRect(wEl, container);
      const b = relRect(rEl, container);
      if (!a || !b) continue;
      const x1 = a.x + a.w / 2;
      const y1 = a.y + a.h; // bottom-centre of the word tile
      const x2 = b.x + b.w / 2;
      const y2 = b.y; // top-centre of the root tile
      const midY = (y1 + y2) / 2;
      const d = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
      const len = Math.hypot(x2 - x1, y2 - y1) + Math.abs(y2 - y1); // generous for dash
      next.push({ d, len, color: RADICAL_COLORS[rm.radicalIndex % RADICAL_COLORS.length], ri: rm.radicalIndex });
    }
    setBeams(next);
  }, [align]);

  useLayoutEffect(() => {
    // A new width re-sizes the word tiles, so measure only once that render has
    // landed (it re-runs this effect) rather than against the stale layout.
    const el = containerRef.current;
    const update = () => {
      const width = el ? el.clientWidth : 0;
      if (width !== rowWidth) setRowWidth(width);
      else measure();
    };
    update();
    // Watch the container rather than the window: its width can change with no
    // resize event, or settle well after one (App transitions its padding-left
    // as the sidebar shows, hides or collapses, e.g. when a phone rotates).
    if (el && typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [measure, playKey, rowWidth]);

  if (!align) {
    // Couldn't align the letters — offer the root plainly so the feature never
    // regresses the existing "did you mean" behaviour.
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, width: "100%" }}>
        <Typography sx={{ color: "#777", fontSize: "0.85rem" }}>{tentative ? "Possible root:" : "Related root:"}</Typography>
        <Button
          onClick={() => onExplore?.(root)}
          variant="outlined"
          sx={{ fontFamily: "inherit", fontWeight: 700, fontSize: "1.4rem", color: "#1535BD", borderColor: "#1535BD", borderRadius: "16px", direction: "rtl", px: 2 }}
        >
          {root}
        </Button>
      </Box>
    );
  }

  const anim = (delay, name, dur = 320) =>
    reduced ? "none" : `${name} ${dur}ms cubic-bezier(0.34,1.56,0.64,1) ${delay}ms both`;

  const tile = (radical, size, color) => ({
    minWidth: size,
    height: size,
    px: 0.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: `${Math.min(14, Math.round((size * 14) / WORD_TILE))}px`,
    fontSize: size > 46 ? "1.9rem" : `${Math.min(1.5, (size * 0.6) / 16)}rem`,
    fontWeight: 700,
    lineHeight: 1,
    userSelect: "none",
    ...(radical
      ? { bgcolor: color, color: "#fff", boxShadow: `0 6px 16px ${color}44` }
      : { bgcolor: "#eef0f5", color: "#9aa0ad" }),
  });

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.25 }}>
      <Box key={playKey} ref={containerRef} sx={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, py: 1 }}>
        {/* connector beams */}
        <Box component="svg" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible", zIndex: 0 }}>
          {beams.map((b, i) => (
            <path
              key={i}
              d={b.d}
              fill="none"
              stroke={b.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              style={{
                strokeDasharray: b.len,
                "--beam-len": `${b.len}px`,
                strokeDashoffset: reduced ? 0 : b.len,
                opacity: reduced ? 0.6 : 0,
                animation: reduced ? "none" : `rootBeamDraw 520ms ease-out ${ROOT_BASE + b.ri * ROOT_STAGGER + 40}ms forwards`,
              }}
            />
          ))}
        </Box>

        {/* the surface word (RTL so it reads right-to-left), always on one row */}
        <Box sx={{ direction: "rtl", display: "flex", justifyContent: "center", gap: `${wordGap}px`, zIndex: 1 }}>
          {align.letters.map((l, i) => {
            const color = l.role === "radical" ? RADICAL_COLORS[l.radicalIndex % RADICAL_COLORS.length] : null;
            return (
              <Box
                key={i}
                ref={(el) => (wordRefs.current[i] = el)}
                sx={{
                  ...tile(l.role === "radical", wordTile, color),
                  // Exactly wordTile wide (not just min-width) so fitWordTiles' row width holds.
                  width: wordTile,
                  flex: "none",
                  animation:
                    l.role === "radical"
                      ? `${anim(WORD_BASE + i * WORD_STAGGER, "rootTileIn", WORD_DUR)}${reduced ? "" : `, rootRadicalPop 420ms ease-out ${ROOT_BASE + l.radicalIndex * ROOT_STAGGER}ms both`}`
                      : anim(WORD_BASE + i * WORD_STAGGER, "rootTileIn", WORD_DUR),
                }}
              >
                {l.char}
              </Box>
            );
          })}
        </Box>

        {/* The root itself is the single primary action: a tappable pill of the
            colour-matched radical tiles with an open arrow. The gap above it is
            left clear for the connectors. */}
        <Box
          role="button"
          tabIndex={0}
          onClick={() => onExplore?.(root)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onExplore?.(root);
            }
          }}
          aria-label={`Open the root ${align.root.map((r) => r.char).join(" ")}`}
          sx={{
            display: "flex", alignItems: "center", gap: 1, direction: "ltr", zIndex: 1, mt: 4,
            cursor: "pointer", borderRadius: "16px", px: 1.25, py: 0.6,
            border: "1.5px solid rgba(21,53,189,0.22)", bgcolor: "rgba(21,53,189,0.05)",
            transition: "transform 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
            "&:hover": { transform: "translateY(-2px)", borderColor: "rgba(21,53,189,0.55)", bgcolor: "rgba(21,53,189,0.09)" },
            "&:focus-visible": { outline: "2px solid #1535BD", outlineOffset: 2 },
          }}
        >
          <Box sx={{ direction: "rtl", display: "flex", gap: 0.75 }}>
            {align.root.map((r, ri) => {
              const color = RADICAL_COLORS[r.radicalIndex % RADICAL_COLORS.length];
              return (
                <Box
                  key={ri}
                  ref={(el) => (rootRefs.current[r.radicalIndex] = el)}
                  sx={{ ...tile(true, 50, color), animation: anim(ROOT_BASE + r.radicalIndex * ROOT_STAGGER, "rootAssemble", 420) }}
                >
                  {r.char}
                </Box>
              );
            })}
          </Box>
          <ArrowForwardRoundedIcon sx={{ color: "#1535BD", fontSize: 20, flex: "none" }} />
        </Box>

        {/* label row: "its root" captions the pill from below, out of the
            connectors' path, with a small replay affordance at the end */}
        <Box sx={{ position: "relative", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", mt: 0.25, zIndex: 1 }}>
          <Typography sx={{ color: "#9aa0ad", fontSize: "0.72rem", letterSpacing: "0.14em", textTransform: "uppercase", opacity: reduced ? 0.85 : 0, animation: reduced ? "none" : `rootLabelIn 300ms ease-out ${ROOT_BASE - 120}ms forwards` }}>
            {tentative ? "possible root" : "its root"}
          </Typography>
          {!reduced && (
            <IconButton
              onClick={() => setPlayKey((k) => k + 1)}
              size="small"
              aria-label="Replay animation"
              // A 48px touch target around the 17px icon, which stays where the old
              // 4px padding put it: the extra padding reaches past the row's end.
              sx={{ position: "absolute", right: "-11.5px", color: "#c4c8d2", p: "15.5px", "&:hover": { color: "#1535BD", bgcolor: "transparent" } }}
            >
              <ReplayRoundedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Typography sx={{ color: "#6b7280", fontSize: "0.82rem", textAlign: "center", lineHeight: 1.55, maxWidth: 300 }}>
        Coloured letters are the <strong>root</strong>; grey letters are the pattern that shapes it into{" "}
        <Box component="span" sx={{ direction: "rtl", unicodeBidi: "isolate", fontWeight: 600, color: "#3a3f4a" }}>{word}</Box>.
      </Typography>
    </Box>
  );
};

export default RootReveal;
